import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import {
  LOCALE_PREFERENCE_STATUS,
  isLocalePreferenceUnauthorizedError,
  isLocalePreferenceUnavailableError,
  localePreferenceEndpoint,
  readPreferredLocale,
  serializeLocalePreference,
} from '../_helpers/localePreference.js';
import { canonicalizeLocale, resolveLocale } from '../locales/index.js';

const { IDLE, LOADING, READY, ERROR, UNAVAILABLE, UNAUTHORIZED } = LOCALE_PREFERENCE_STATUS;

/** Statuses under which the account will not take a choice, so none is sent. */
const ACCOUNT_REFUSES = [UNAVAILABLE, UNAUTHORIZED];

/**
 * The signed-in listener's locale preference (ENG-379), layered over the
 * device copy that `LocaleProvider` keeps.
 *
 * While `enabled` (a session is held) the account preference is loaded once
 * and, when it names a locale this build can activate, applied through
 * `setLocale` — which also mirrors it to the device, so the next load starts
 * there. A choice made through `choose` applies at once on the device (the
 * page must not wait on the network to change language) and is then saved to
 * the account; a failed save keeps the device's choice and hands back a retry
 * for the same tag, since the alternative — snapping the page back — would
 * undo what the listener can see worked. The failure stays on show while its
 * retry is in flight (`saving` and `saveError` together mean "retrying"), so
 * the control that offered it is not pulled from under the listener before
 * the answer; only a save that lands, a fresh choice or the account refusing
 * clears it. A save the account refuses (401/403) or cannot take (404/501)
 * is a status, not a failure to retry: both would fail the same way again.
 * Two races are settled in the listener's favour: a choice made while the
 * load is in flight is newer than the answer, so the answer is not applied
 * over it, and only the latest of overlapping saves reports its outcome.
 *
 * `setLocale` is the device layer's: it refuses (returns false for) a tag it
 * cannot activate, and `choose` passes that refusal through unchanged.
 */
export function useLocalePreference({ enabled, locale, setLocale }) {
  const fetchWrapper = useFetchWrapper();
  const fetchWrapperRef = useRef(fetchWrapper);
  const [status, setStatus] = useState(enabled ? LOADING : IDLE);
  const [saving, setSaving] = useState(false);
  // `{ locale, retry }` for the latest save that failed, else null.
  const [saveError, setSaveError] = useState(null);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const statusRef = useRef(status);
  statusRef.current = status;
  // Set by `choose` so a load that finishes afterwards does not apply a stale answer.
  const chosenRef = useRef(false);
  // Sequence of saves; only the latest one's outcome is reported.
  const saveSeqRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setStatus(IDLE);
      setSaving(false);
      setSaveError(null);
      return undefined;
    }
    let cancelled = false;
    chosenRef.current = false;
    setStatus(LOADING);
    const load = async () => {
      try {
        const response = await fetchWrapperRef.current.get(localePreferenceEndpoint());
        if (cancelled) return;
        const preferred = readPreferredLocale(response);
        // The current locale is always activatable, so a tag this build cannot
        // offer (nor match by language) resolves to it and changes nothing.
        if (preferred && !chosenRef.current) setLocale(resolveLocale([preferred, localeRef.current]));
        setStatus(READY);
      } catch (err) {
        if (cancelled) return;
        if (isLocalePreferenceUnavailableError(err)) {
          setStatus(UNAVAILABLE);
        } else if (isLocalePreferenceUnauthorizedError(err)) {
          setStatus(UNAUTHORIZED);
        } else {
          console.error('Failed to load locale preference:', err);
          setStatus(ERROR);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, setLocale]);

  const save = useCallback(async (tag) => {
    const seq = ++saveSeqRef.current;
    const latest = () => seq === saveSeqRef.current && enabledRef.current;
    // A retry leaves its failure standing until the answer comes back (see above).
    setSaving(true);
    try {
      await fetchWrapperRef.current.put(localePreferenceEndpoint(), serializeLocalePreference(tag));
      if (!latest()) return true;
      // A save that went through proves the account takes choices, whatever the load said.
      setStatus(READY);
      setSaving(false);
      setSaveError(null);
      return true;
    } catch (err) {
      if (!latest()) return false;
      setSaving(false);
      if (isLocalePreferenceUnavailableError(err)) {
        // Not shipped here after all: nothing to retry, the device copy stands.
        setStatus(UNAVAILABLE);
        setSaveError(null);
      } else if (isLocalePreferenceUnauthorizedError(err)) {
        // The session no longer reaches the account (expired between the load
        // and this choice, or not a listener): a retry would meet the same
        // door, so the choice stays on the device, as after a refused load.
        setStatus(UNAUTHORIZED);
        setSaveError(null);
      } else {
        console.error('Failed to save locale preference:', err);
        setSaveError({ locale: tag, retry: () => save(tag) });
      }
      return false;
    }
  }, []);

  /** Apply `tag` on the device and, when signed in and the account takes choices, save it there. */
  const choose = useCallback((tag) => {
    if (!setLocale(tag)) return false;
    chosenRef.current = true;
    setSaveError(null);
    if (enabledRef.current && !ACCOUNT_REFUSES.includes(statusRef.current)) save(canonicalizeLocale(tag));
    return true;
  }, [setLocale, save]);

  return { status, saving, saveError, choose };
}
